import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { isPast } from "date-fns"
import { Pencil, Trash } from "react-bootstrap-icons"
import { ShowDateFormat } from "../../util/dates"
import { Currency } from "../../util/numbers"
import { useTicketPurchasesForPost } from "../../hooks/useTicketPurchases"
import { useDonationsForPost } from "../../hooks/useDonationsForPost"
import { useReactions } from "../../hooks/useReactions"
import { usePerformersForPost } from "@hooks/usePerformersForPost"
import { formatInterestCount } from "./formatInterestCount"
import { FeedSpinner } from "../../components/shared/Spinner"
import { useQueryClient } from "react-query"
import { destroyPost } from "../../util/backend_api/posts"
import { BAND_POSTS_QUERY_KEY } from "../../hooks/useBandPosts"

interface ShowPerformer {
  event_performance_id: number
  band_id: number | null
  name: string
  email: string | null
  bandnada_url: string | null
  tickets_sold: number
  interest_count: number | null
  interest_percentage: number | null
  extrapolated_tickets_sold: number | null
}

const PerformerSubRow: React.FC<{ performer: ShowPerformer }> = ({
  performer,
}) => (
  <li className="grid grid-cols-12 lg:grid-cols-24 items-center border-l-2 border-l-green-600 bg-gray-50 py-1.5 text-xs text-gray-700">
    <div className="col-span-5 lg:col-span-10 pl-6 truncate">
      {performer.bandnada_url ? (
        <a href={performer.bandnada_url} className="hover:underline" target="_blank" rel="noreferrer">
          {performer.name}
        </a>
      ) : (
        performer.name
      )}
    </div>
    <div className="col-span-4 lg:col-span-6">
      <div className="text-[10px] uppercase text-gray-400">Interested</div>
      {formatInterestCount(performer.interest_count, performer.interest_percentage)}
    </div>
    <div className="col-span-3 lg:col-span-4">
      <div className="text-[10px] uppercase text-gray-400">Extrap. Sold</div>
      {performer.extrapolated_tickets_sold ?? "—"}
    </div>
    <div className="hidden lg:block lg:col-span-4" />
  </li>
)

const PerformersBreakdown: React.FC<{ bandId: number; postId: number }> = ({
  bandId,
  postId,
}) => {
  const { data, status } = usePerformersForPost(bandId, postId, true)

  if (status === "loading") {
    return (
      <li className="grid grid-cols-12 items-center bg-gray-50 py-2 pl-6 text-xs text-gray-500">
        <div className="col-span-12">
          <FeedSpinner />
        </div>
      </li>
    )
  }

  if (status === "error" || !data?.performers?.length) {
    return (
      <li className="grid grid-cols-12 items-center bg-gray-50 py-2 pl-6 text-xs text-gray-500">
        <div className="col-span-12">No performers tagged on this show.</div>
      </li>
    )
  }

  return (
    <>
      {data.performers.map((performer: ShowPerformer) => (
        <PerformerSubRow key={performer.event_performance_id} performer={performer} />
      ))}
    </>
  )
}

const TICKET_TYPE_LABELS: Record<string, string> = {
  no_advance_tickets: "No Advance Tkts",
  bNTickets: "bandNada Tkts",
  externalTickets: "External Tkts",
}

interface PostCollaborator {
  band_id: number
  status: string
}

interface Post {
  id: number
  band_id?: number
  post_type?: string
  active_date: string
  published_at?: string
  title: string
  status?: string
  canceled_at?: string | null
  end_dt?: string | null
  nada_path?: string
  nada_url?: string
  ticket_type?: string
  band_nada_tickets_sold?: number
  band_nada_ticket_max?: number
  ticket_price?: number
  accepts_donation?: boolean
  post_collaborators?: PostCollaborator[]
  external_url?: string
  external_photo_src?: string
  content_hash?: { location?: string; ticket_price?: string }
}

interface RecentShowRowProps {
  post: Post
  currentBandId?: number
  onEdit?: (post: Post) => void
}

export const RecentShowRow: React.FC<RecentShowRowProps> = ({
  post,
  currentBandId,
  onEdit,
}) => {
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const isOwner =
    currentBandId != null &&
    post.band_id != null &&
    post.band_id === currentBandId
  const isCollaborator =
    !isOwner &&
    currentBandId != null &&
    (post.post_collaborators ?? []).some(
      (c) => c.band_id === currentBandId && c.status === "accepted",
    )
  const isExternalEventUrl = post.post_type === "external_event_url"
  const postUrl = post.nada_path || post.nada_url || "#"
  // Only the show's owner can see its ticket/interest breakdown (that's all
  // the performers#for_post endpoint authorizes) - a collaborator-tagged
  // show isn't one of currentBandId's own posts, so it's excluded here too.
  const canExpandPerformers = isOwner && !isExternalEventUrl

  const handleDeleteExternalEvent = async () => {
    if (currentBandId == null) return
    if (!window.confirm("Are you sure you want to delete this external event?"))
      return
    setIsDeleting(true)
    try {
      await destroyPost(post.id, currentBandId)
      const queryKey = [BAND_POSTS_QUERY_KEY, currentBandId]
      queryClient.invalidateQueries({ queryKey })
      await queryClient.refetchQueries({ queryKey })
    } finally {
      setIsDeleting(false)
    }
  }

  // Fetch ticket purchases for this post
  const { data: ticketData, status: ticketStatus } = useTicketPurchasesForPost(
    post.id,
    {},
    { enabled: !!post.id && post.ticket_type === "bNTickets" },
  )

  // Fetch donations for this post
  const { data: donationsData, status: donationsStatus } = useDonationsForPost(
    post.id,
    !!post.id && !!post.accepts_donation,
  )

  // Fetch RSVP count using useReactions (same as InterestedBlock)
  const { data: reactionsData, status: reactionsStatus } = useReactions(
    "Post",
    post.id,
    "rsvps",
    true,
  )

  // Calculate share totals from ticket purchases
  const shareTotals = useMemo(() => {
    if (!ticketData?.tickets) {
      return { band_share: 0, bn_share: 0, promotion_share: 0 }
    }

    return ticketData.tickets.reduce(
      (totals, ticket) => {
        // Only count tickets that are charged/paid (not refunded or cancelled)
        if (
          ticket.status === "charged" ||
          ticket.status === "charged_and_paid" ||
          ticket.status === "confirmed_by_user"
        ) {
          totals.band_share += ticket.band_share || 0
          totals.bn_share += ticket.bn_share || 0
          totals.promotion_share += ticket.promotion_share || 0
        }
        return totals
      },
      { band_share: 0, bn_share: 0, promotion_share: 0 },
    )
  }, [ticketData])

  // Get RSVP count from reactions data
  const rsvpCount = reactionsData?.total_count ?? 0

  // Calculate ticket purchase display
  const ticketDisplay = useMemo(() => {
    const sold = post.band_nada_tickets_sold ?? 0
    const max = post.band_nada_ticket_max ?? 0

    if (max === 0) {
      // Unlimited tickets
      return `${sold}/∞`
    }

    const percentSold = max > 0 ? Math.round((sold / max) * 100) : 0
    return `${sold} of ${max} (${percentSold}%)`
  }, [post.band_nada_tickets_sold, post.band_nada_ticket_max])

  const isCanceled = post.status === "canceled" || !!post.canceled_at
  const strikeClass = isCanceled ? "line-through" : ""

  const isPastShow =
    (post.end_dt != null && isPast(new Date(post.end_dt))) ||
    (post.active_date != null && isPast(new Date(post.active_date)))

  return (
    <>
      <li className="grid grid-cols-12 lg:grid-cols-24 items-center py-2">
        <div
          className={`col-span-10 ${canExpandPerformers ? "cursor-pointer" : ""}`}
          onClick={() => canExpandPerformers && setExpanded((e) => !e)}
        >
          <div>{post.active_date ? ShowDateFormat(post.active_date) : "—"}</div>
          <div
            className={`text-sm flex items-center gap-1 ${isCanceled ? "text-bn-neon-red" : ""}`}
          >
            {canExpandPerformers && (
              <span
                className={`inline-block w-3 shrink-0 text-gray-400 transition-transform ${
                  expanded ? "rotate-90" : ""
                }`}
              >
                ›
              </span>
            )}
            {isCanceled ? (
              <>
                Canceled -{" "}
                <span className="line-through">{post.title || "—"}</span>
              </>
            ) : (
              post.title || "—"
            )}
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-2">
          {post.ticket_price ? Currency(post.ticket_price) : "—"}
        </div>
        <div
          className={`hidden lg:block lg:col-span-2 text-sm ${
            post.ticket_type === "bNTickets" ? "text-green-700" : ""
          }`}
        >
          {post.ticket_type
            ? (TICKET_TYPE_LABELS[post.ticket_type] ?? post.ticket_type)
            : "—"}
        </div>
        <div className="hidden lg:block lg:col-span-2">
          {reactionsStatus === "loading" ? (
            <FeedSpinner />
          ) : (
            <>
              {rsvpCount}
              {!!reactionsData?.referral_count && (
                <div className="text-xs text-green-700">
                  {reactionsData.referral_count} via{" "}
                  <span className="font-bold">bN</span> referrals
                </div>
              )}
            </>
          )}
        </div>
        <div className={`hidden lg:block lg:col-span-2 ${strikeClass}`}>
          {ticketDisplay}
          {!!ticketData?.referral_count && (
            <div className="text-xs text-green-700">
              {ticketData.referral_count} via{" "}
              <span className="font-bold">bN</span> referrals
            </div>
          )}
        </div>
        <div className={`hidden lg:block lg:col-span-2 ${strikeClass}`}>
          {ticketStatus === "loading" ? (
            <FeedSpinner />
          ) : (
            Currency(
              shareTotals.band_share +
                shareTotals.bn_share +
                shareTotals.promotion_share,
            )
          )}
        </div>
        <div className={`hidden lg:block lg:col-span-2 ${strikeClass}`}>
          {!post.accepts_donation ? (
            "—"
          ) : donationsStatus === "loading" ? (
            <FeedSpinner />
          ) : (
            <>
              {donationsData?.total_count ?? 0} donation
              {donationsData?.total_count === 1 ? "" : "s"}
              <div className="text-xs text-gray-500">
                {Currency(donationsData?.total_amount ?? 0)}
              </div>
            </>
          )}
        </div>
        <div className="col-span-2 flex flex-wrap gap-2 items-center">
          {(post.nada_path || post.nada_url) && (
            <Link to={postUrl} className="text-blue-600 hover:underline text-xs">
              View
            </Link>
          )}
          {((isOwner || isCollaborator) &&
            !isExternalEventUrl &&
            (!isPastShow || post.ticket_type === "bNTickets")) ||
          (isOwner && isExternalEventUrl && currentBandId != null) ? (
            <span className="text-gray-400">|</span>
          ) : null}
          {(isOwner || isCollaborator) && !isExternalEventUrl && (
            <>
              {!isPastShow && (
                <>
                  <Link
                    to={`${postUrl}?edit=true`}
                    className="text-blue-600 hover:underline text-xs"
                  >
                    Edit
                  </Link>
                  {post.ticket_type === "bNTickets" && (
                    <span className="text-gray-400">|</span>
                  )}
                </>
              )}
              {post.ticket_type === "bNTickets" && (
                <Link
                  to={`/manage-tickets/${post.id}`}
                  className="text-blue-600 hover:underline text-xs"
                >
                  Manage
                </Link>
              )}
            </>
          )}
          {isOwner && isExternalEventUrl && currentBandId != null && (
            <>
              <button
                type="button"
                onClick={() => onEdit?.(post)}
                className="p-1 text-gray-600 hover:text-blue-600 focus:outline-none"
                title="Edit external event"
                aria-label="Edit external event"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleDeleteExternalEvent}
                disabled={isDeleting}
                className="p-1 text-gray-600 hover:text-red-600 focus:outline-none disabled:opacity-50"
                title="Delete external event"
                aria-label="Delete external event"
              >
                <Trash className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </li>
      {expanded && canExpandPerformers && currentBandId != null && (
        <PerformersBreakdown bandId={currentBandId} postId={post.id} />
      )}
      <hr className="border-gray-300" />
    </>
  )
}
